import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import EditIcon from "@mui/icons-material/Edit";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import AddMaterialsDialog from "./AddMaterialsDialog";
import EditeMaterialsDialog from "./EditeMaterialsDialog";
import { getMaterialsList, updateMaterial } from "../../../../../services/Admin/MaterialsApi";
import { useAuth } from "../../../../../Context/AuthContext";

const priorityOptions = [
  { value: "A", label: "تصنيف أول" },
  { value: "B", label: "تصنيف ثاني" },
  { value: "C", label: "تصنيف ثالث" },
  { value: "D", label: "تصنيف رابع" },
  { value: "Z", label: "تصنيف خامس" },
];

const priorityLabels = {
  a: "تصنيف أول",
  b: "تصنيف ثاني",
  c: "تصنيف ثالث",
  d: "تصنيف رابع",
  z: "تصنيف خامس",
};

const getPriorityLabel = (priority) => {
  const key = String(priority || "").trim().toLowerCase();
  return priorityLabels[key] || "-";
};

const getPriorityKey = (priority) => String(priority || "").trim().toLowerCase();

const getPriorityValue = (priority) => {
  const value = String(priority || "A").trim().toUpperCase();
  return ["A", "B", "C", "D", "Z"].includes(value) ? value : "A";
};

const normalizeKeywords = (value) => {
  if (Array.isArray(value)) {
    return value.map((keyword) => String(keyword).trim()).filter(Boolean);
  }

  return String(value || "")
    .split(/[\s,،;؛]+/)
    .map((keyword) => keyword.trim())
    .filter(Boolean);
};

const formatKeywords = (value) => normalizeKeywords(value).join(", ");

const getMaterialRules = (material) => {
  const rawRules = material?.rules;

  if (Array.isArray(rawRules)) {
    return rawRules.map((rule) => String(rule).trim()).filter(Boolean);
  }

  if (typeof rawRules === "string") {
    try {
      const parsedRules = JSON.parse(rawRules);
      if (Array.isArray(parsedRules)) {
        return parsedRules.map((rule) => String(rule).trim()).filter(Boolean);
      }
    } catch (error) {
      // fallback to line parsing below
    }

    return rawRules
      .split(/\r?\n/)
      .map((rule) => rule.trim())
      .filter(Boolean);
  }

  return [];
};

const buildMaterialsExportRows = (materialsList = []) =>
  materialsList.map((material, index) => ({
    "م": index + 1,
    "المعرف": material?.id ?? "",
    "الاسم": material?.name || "",
    "الاسم بالإنجليزي": material?.name_en || "",
    "الوحدة": material?.measurment_unit?.name || material?.measurement_unit?.name || "",
    "التصنيف": getPriorityLabel(material?.priority),
    "كود التصنيف": material?.priority || "",
    "سعر الوحدة": material?.cost_price_per_unit ?? "",
    "أقصى استخدام": material?.max_usage_per_product ?? "",
    "الكلمات المفتاحية": formatKeywords(material?.keywords),
    "القواعد": getMaterialRules(material)
      .map((rule, ruleIndex) => `${ruleIndex + 1}. ${rule}`)
      .join("\n"),
    "الحالة": Number(material?.status) === 0 ? "غير متاح" : "متاح",
  }));

const MaterialsTab = () => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editMaterial, setEditMaterial] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [unitFilter, setUnitFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("active");
  const [editingPriorityId, setEditingPriorityId] = useState(null);
  const [savingPriorityId, setSavingPriorityId] = useState(null);
  const [priorityUpdateError, setPriorityUpdateError] = useState("");
  const { hasPermission, userRole, userType } = useAuth();

  const canViewDeleted = userType === "admin" && userRole === "admin";

  const unitOptions = useMemo(() => {
    const map = new Map();
    materials.forEach((material) => {
      const id =
        material?.measurment_unit?.id ||
        material?.measurement_unit?.id ||
        material?.measurment_unit_id ||
        material?.measurement_unit_id;
      const name = material?.measurment_unit?.name || material?.measurement_unit?.name;

      if (id && name && !map.has(String(id))) {
        map.set(String(id), name);
      }
    });

    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [materials]);

  const filteredMaterials = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return materials.filter((material) => {
      const isDeleted = Number(material?.status) === 0;

      if (activeTab === "deleted") {
        if (!canViewDeleted || !isDeleted) return false;
      } else if (isDeleted) {
        return false;
      }

      const materialUnitId = String(
        material?.measurment_unit?.id ||
          material?.measurement_unit?.id ||
          material?.measurment_unit_id ||
          material?.measurement_unit_id ||
          ""
      );

      if (unitFilter !== "all" && materialUnitId !== unitFilter) {
        return false;
      }

      const materialPriorityKey = getPriorityKey(material?.priority);
      const materialHasPriority = Boolean(priorityLabels[materialPriorityKey]);
      if (priorityFilter === "none" && materialHasPriority) {
        return false;
      }
      if (priorityFilter !== "all" && priorityFilter !== "none" && materialPriorityKey !== priorityFilter.toLowerCase()) {
        return false;
      }

      const statusValue = material?.status ? "active" : "inactive";
      if (statusFilter !== "all" && statusValue !== statusFilter) {
        return false;
      }

      if (!query) return true;

      const keywordsValue = Array.isArray(material?.keywords)
        ? material.keywords.join(", ")
        : material?.keywords || "";
      const rulesValue = getMaterialRules(material).join(" ");

      const searchableText = [
        material?.id,
        material?.name,
        material?.name_en,
        material?.measurment_unit?.name,
        material?.measurement_unit?.name,
        material?.cost_price_per_unit,
        material?.max_usage_per_product,
        getPriorityLabel(material?.priority),
        material?.priority,
        keywordsValue,
        rulesValue,
        material?.status ? "متاح" : "غير متاح",
      ]
        .map((value) => String(value ?? ""))
        .join(" ")
        .toLowerCase();

      return searchableText.includes(query);
    });
  }, [activeTab, canViewDeleted, materials, searchQuery, unitFilter, priorityFilter, statusFilter]);

  useEffect(() => {
    if (!canViewDeleted && activeTab === "deleted") {
      setActiveTab("active");
    }
  }, [activeTab, canViewDeleted]);

  const fetchMaterials = async () => {
    setLoading(true);
    setError("");

    try {
      const res = await getMaterialsList();
      setMaterials(res?.data || []);
    } catch (err) {
      setError(err?.message || "Failed to load materials");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    if (mounted) fetchMaterials();
    return () => {
      mounted = false;
    };
  }, []);

  const buildMaterialUpdatePayload = (material, nextPriority) => ({
    id: material.id,
    name: String(material?.name || ""),
    name_en: String(material?.name_en || ""),
    measurement_unit_id: Number(
      material?.measurment_unit_id ||
        material?.measurement_unit_id ||
        material?.measurment_unit?.id ||
        material?.measurement_unit?.id ||
        0
    ) || null,
    priority: nextPriority,
    cost_price_per_unit: Number(material?.cost_price_per_unit) || 0,
    status: Number(material?.status) !== 0,
    stock_quantity:
      material?.stock_quantity === null || material?.stock_quantity === undefined || material?.stock_quantity === ""
        ? null
        : Number(material.stock_quantity),
    max_usage_per_product: Number(material?.max_usage_per_product) || 0,
    min_stock_threshold:
      material?.min_stock_threshold === null || material?.min_stock_threshold === undefined || material?.min_stock_threshold === ""
        ? null
        : Number(material.min_stock_threshold),
    keywords: normalizeKeywords(material?.keywords),
    rules: getMaterialRules(material),
  });

  const handlePriorityEditStart = (material) => {
    if (!hasPermission("edit") || savingPriorityId === material?.id) return;
    setEditingPriorityId(material.id);
    setPriorityUpdateError("");
  };

  const handlePriorityChange = async (material, nextPriority) => {
    const normalizedPriority = getPriorityValue(nextPriority);
    if (getPriorityValue(material?.priority) === normalizedPriority) {
      setEditingPriorityId(null);
      return;
    }

    setSavingPriorityId(material.id);
    setPriorityUpdateError("");

    try {
      const res = await updateMaterial(buildMaterialUpdatePayload(material, normalizedPriority));
      const updatedMaterial = res?.data && !Array.isArray(res.data) ? res.data : null;

      setMaterials((prev) =>
        prev.map((item) =>
          item.id === material.id
            ? {
                ...item,
                ...(updatedMaterial || {}),
                priority: updatedMaterial?.priority || normalizedPriority,
              }
            : item
        )
      );
      setEditingPriorityId(null);
    } catch (err) {
      setPriorityUpdateError(err?.message || "فشل تحديث التصنيف");
    } finally {
      setSavingPriorityId(null);
    }
  };

  const handleExportAllMaterials = () => {
    const exportRows = buildMaterialsExportRows(materials);
    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    worksheet["!cols"] = [
      { wch: 8 },
      { wch: 12 },
      { wch: 28 },
      { wch: 28 },
      { wch: 18 },
      { wch: 16 },
      { wch: 12 },
      { wch: 14 },
      { wch: 16 },
      { wch: 34 },
      { wch: 52 },
      { wch: 12 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "الخامات");
    XLSX.writeFile(workbook, `materials_export_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Box sx={{ color: "error.main", p: 2 }}>{String(error)}</Box>;
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          mb: 2,
          flexWrap: "wrap",
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(event, value) => setActiveTab(value)}
          aria-label="materials tabs"
        >
          <Tab label="الخامات" value="active" />
          {canViewDeleted && <Tab label="المحذوفات" value="deleted" />}
        </Tabs>

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {hasPermission("export") && (
            <Button
              variant="outlined"
              color="success"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportAllMaterials}
              disabled={!materials.length}
            >
              تصدير كل البيانات
            </Button>
          )}

          {activeTab === "active" && hasPermission("add") && (
            <Button variant="contained" onClick={() => setAddDialogOpen(true)}>
              إضافة خامة
            </Button>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 220px 190px 180px" },
          gap: 1.5,
          mb: 2,
        }}
      >
        <TextField
          label="بحث في كل بيانات الخامات"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          fullWidth
        />

        <FormControl fullWidth>
          <InputLabel id="materials-unit-filter-label">فلتر الوحدة</InputLabel>
          <Select
            labelId="materials-unit-filter-label"
            label="فلتر الوحدة"
            value={unitFilter}
            onChange={(e) => setUnitFilter(e.target.value)}
          >
            <MenuItem value="all">كل الوحدات</MenuItem>
            {unitOptions.map((unit) => (
              <MenuItem key={unit.id} value={unit.id}>
                {unit.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel id="materials-priority-filter-label">فلتر التصنيف</InputLabel>
          <Select
            labelId="materials-priority-filter-label"
            label="فلتر التصنيف"
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
          >
            <MenuItem value="all">كل التصنيفات</MenuItem>
            <MenuItem value="none">بدون تصنيف</MenuItem>
            {priorityOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel id="materials-status-filter-label">فلتر الحالة</InputLabel>
          <Select
            labelId="materials-status-filter-label"
            label="فلتر الحالة"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">كل الحالات</MenuItem>
            <MenuItem value="active">متاح</MenuItem>
            <MenuItem value="inactive">غير متاح</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {priorityUpdateError && (
        <Box sx={{ color: "error.main", mb: 2, fontWeight: 700 }}>
          {priorityUpdateError}
        </Box>
      )}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>م</TableCell>
              <TableCell>الاسم</TableCell>
              <TableCell>الاسم بالإنجليزي</TableCell>
              <TableCell>الوحدة</TableCell>
              <TableCell>التصنيف</TableCell>
              <TableCell>سعر الوحدة</TableCell>
              <TableCell>أقصى استخدام</TableCell>
              <TableCell>الكلمات المفتاحية</TableCell>
              <TableCell>القواعد</TableCell>
              <TableCell>الحالة</TableCell>
              {hasPermission("edit") && <TableCell align="right">إجراءات</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredMaterials.map((material, index) => (
              <TableRow
                key={material.id}
                hover
                sx={
                  material?.status
                    ? undefined
                    : {
                        backgroundColor: "#fff5f5",
                        "&:hover": {
                          backgroundColor: "#ffe8e8",
                        },
                      }
                }
              >
                <TableCell>{index + 1}</TableCell>
                <TableCell sx={material?.status ? undefined : { color: "error.main", fontWeight: 700 }}>
                  {material.name}
                </TableCell>
                <TableCell>{material.name_en || "-"}</TableCell>
                <TableCell>{material.measurment_unit?.name || material.measurement_unit?.name || "-"}</TableCell>
                <TableCell
                  onDoubleClick={() => handlePriorityEditStart(material)}
                  sx={{ minWidth: 145, cursor: hasPermission("edit") ? "pointer" : "default" }}
                  title={hasPermission("edit") ? "اضغط مرتين لتعديل التصنيف" : undefined}
                >
                  {editingPriorityId === material.id ? (
                    <FormControl size="small" fullWidth>
                      <Select
                        autoFocus
                        value={getPriorityValue(material?.priority)}
                        onChange={(e) => handlePriorityChange(material, e.target.value)}
                        onClose={() => {
                          if (savingPriorityId !== material.id) setEditingPriorityId(null);
                        }}
                        disabled={savingPriorityId === material.id}
                      >
                        {priorityOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                      <Chip
                        size="small"
                        label={getPriorityLabel(material?.priority)}
                        variant="outlined"
                        sx={{ fontWeight: 800 }}
                      />
                      {savingPriorityId === material.id && <CircularProgress size={15} />}
                    </Box>
                  )}
                </TableCell>
                <TableCell>{material.cost_price_per_unit}</TableCell>
                <TableCell>{material.max_usage_per_product}</TableCell>
                <TableCell>
                  {Array.isArray(material.keywords)
                    ? material.keywords.join(", ")
                    : material.keywords || ""}
                </TableCell>
                <TableCell sx={{ minWidth: 220 }}>
                  {getMaterialRules(material).length ? (
                    <Box component="ol" sx={{ m: 0, pl: 2.5, pr: 2.5 }}>
                      {getMaterialRules(material).map((rule, ruleIndex) => (
                        <Box component="li" key={`${material.id}-rule-${ruleIndex}`} sx={{ mb: 0.35 }}>
                          {rule}
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={material.status ? "متاح" : "غير متاح"}
                    color={material.status ? "success" : "error"}
                    variant={material.status ? "filled" : "outlined"}
                  />
                </TableCell>
                {hasPermission("edit") && (
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      aria-label="edit"
                      onClick={() => {
                        setEditMaterial(material);
                        setEditDialogOpen(true);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {!filteredMaterials.length && (
              <TableRow>
                <TableCell colSpan={hasPermission("edit") ? 11 : 10} align="center" sx={{ py: 3 }}>
                  لا توجد نتائج مطابقة للبحث أو الفلاتر
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <AddMaterialsDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onAdded={fetchMaterials}
      />
      <EditeMaterialsDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        material={editMaterial}
        onUpdated={() => {
          fetchMaterials();
          setEditDialogOpen(false);
        }}
      />
    </Box>
  );
};

export default MaterialsTab;
